import {
	Breakpoints,
	Container,
	ContainerGutter,
	ContainerPadding,
	Layout,
	Select,
	styled,
	TextInput,
} from '@volkswagen-onehub/components-core';
import * as React from 'react';
import { PortalContext } from '../../../../context/portal-context';
import {
	validateNoEmpty,
	validatePersonName,
	updateValidateFormField,
	hasFormFieldError,
	generateSelectOptions,
	validateWrongDni,
} from '../../../../helpers/form-helper';
import { useSetState } from '../../../../hooks/get-state-hook';
import { Form, SelectOption, FormDef } from '@esportal/portal-service';
import { TypeDocumentEnum } from '@esportal/portal-service';
import { getSurname1, getSurname2, getUserName } from '../../../../helpers/nameHelper';
interface Props {
	handleChangeData: (form: Form) => void;
}

const FlexContainerSingle = styled.div`
	@media screen and (max-width: 959px) {
		& div > div > div > div {
			flex: 1;
		}
	}
`;

const InputContainer = styled.div`
	& > div {
		border: 1px solid blue;
		width: var(--size-grid007);
	}
`;

const InputContainerDouble = styled.div`
	width: 100%;

	@media screen and (max-width: 959px) {
		& div > div > div {
			flex: 1;
		}
	}

	@media screen and (min-width: 960px) and (max-width: 1279px) {
		width: var(--size-grid014);
	}

	@media screen and (min-width: 1280px) and (max-width: 1599px) {
		width: var(--size-grid012);
	}

	@media screen and (min-width: 1600px) and (max-width: 1919px) {
		width: var(--size-grid009);
	}

	@media screen and (min-width: 1920px) and (max-width: 2559px) {
		width: var(--size-grid008);
	}

	@media screen and (min-width: 2560px) {
		width: calc(var(--size-grid001) * 6.5);
	}
`;

export const typeDocumentOptions: SelectOption[] = [
	{
		label: 'DNI/NIF',
		value: TypeDocumentEnum.DNI_NIF,
	},
	{
		label: 'NIE',
		value: TypeDocumentEnum.NIE,
	},
];

export const CompleteRegisterForm: React.FC<Props> = ({ handleChangeData }) => {
	const { portalService } = React.useContext(PortalContext);

	const userData = portalService.getPerson().getValue();
	const userName = getUserName(userData);
	const userSurname1 = getSurname1(userData);
	const userSurname2 = getSurname2(userData);

	const [form, setForm, getForm] = useSetState<Form>(
		new Form({
			typeDocument: new FormDef(TypeDocumentEnum.DNI_NIF, [validateNoEmpty]),
			document: new FormDef(undefined, [validateNoEmpty, validateWrongDni]),
			name: new FormDef(userName, [validateNoEmpty, validatePersonName]),
			surname1: new FormDef(userSurname1, [validateNoEmpty, validatePersonName]),
			surname2: new FormDef(userSurname2, [validatePersonName]),
		}),
	);

	const handleChange = (e: React.SyntheticEvent<HTMLInputElement> | React.SyntheticEvent<HTMLSelectElement>) => {
		handleFieldChange(e, false);
	};

	const handleBlurField = (e: React.SyntheticEvent<HTMLInputElement> | React.SyntheticEvent<HTMLSelectElement>) => {
		handleFieldChange(e, true);
	};

	const handleFieldChange = (
		e: React.SyntheticEvent<HTMLInputElement> | React.SyntheticEvent<HTMLSelectElement>,
		modeBlur: boolean,
	) => {
		const { name, value } = e.target as HTMLInputElement | HTMLSelectElement;
		const processedValue = name === 'document' ? value.toUpperCase() : value;

		getForm().then((form: Form) => {
			if (!modeBlur || (modeBlur && form.fields[name].virgin)) {
				const formResult: Form = updateValidateFormField(form, name, processedValue, modeBlur);
				setForm(formResult);
				handleChangeData(formResult);
			}
		});
	};

	return (
		<Layout
			allowOverflowingContent
			appearance={{
				[Breakpoints.default]: [
					{ name: '.', columns: 1 },
					{ name: 'a', columns: 22 },
					{ name: '.', columns: 1 },
				],
				[Breakpoints.b960]: [
					{ name: '.', columns: 1 },
					{ name: 'a', columns: 22 },
					{ name: '.', columns: 1 },
				],
				[Breakpoints.b1280]: [
					{ name: '.', columns: 3 },
					{ name: 'a', columns: 18 },
					{ name: '.', columns: 3 },
				],
				[Breakpoints.b1600]: [
					{ name: '.', columns: 5 },
					{ name: 'a', columns: 14 },
					{ name: '.', columns: 5 },
				],
				[Breakpoints.b1920]: [
					{ name: '.', columns: 6 },
					{ name: 'a', columns: 12 },
					{ name: '.', columns: 6 },
				],
				[Breakpoints.b2560]: [
					{ name: '.', columns: 7 },
					{ name: 'a', columns: 10 },
					{ name: '.', columns: 7 },
				],
			}}
		>
			<Container
				data-testid="form-container"
				wrap={'always'}
				gutter={ContainerGutter.dynamic0130}
				padding={{ top: { [Breakpoints.default]: ContainerPadding.dynamic0250 } }}
				stretchContent
			>
				<Container
					data-testid="register-form__name-container"
					wrap={{ [Breakpoints.default]: 'always', [Breakpoints.b960]: 'never' }}
					horizontalAlign={'space-between'}
					gutter={{
						[Breakpoints.default]: ContainerGutter.dynamic0130,
						[Breakpoints.b960]: ContainerGutter.static400,
					}}
					stretchContent
				>
					<Container
						wrap={'never'}
						padding={{
							right: { [Breakpoints.b560]: ContainerPadding.grid012, [Breakpoints.b960]: ContainerPadding.static0 },
						}}
						stretchContent
					>
						<InputContainer>
							<TextInput
								appearance={hasFormFieldError(form.fields.name) ? 'error' : undefined}
								label="Nombre*"
								name="name"
								value={form.fields.name.value}
								onChange={handleChange}
								onBlur={handleBlurField}
							/>
						</InputContainer>
					</Container>
					<FlexContainerSingle className="flex-container">
						<Container
							data-testid="register-form__surname-container"
							wrap={{ [Breakpoints.default]: 'always', [Breakpoints.b560]: 'never' }}
							gutter={{
								[Breakpoints.default]: ContainerGutter.dynamic0130,
								[Breakpoints.b560]: ContainerPadding.static400,
							}}
							horizontalAlign={'space-between'}
							stretchContent
						>
							<InputContainer>
								<TextInput
									appearance={hasFormFieldError(form.fields.surname1) ? 'error' : undefined}
									label="Primer apellido*"
									name="surname1"
									value={form.fields.surname1.value}
									onChange={handleChange}
									onBlur={handleBlurField}
								/>
							</InputContainer>
							<InputContainer>
								<TextInput
									appearance={hasFormFieldError(form.fields.surname2) ? 'error' : undefined}
									label="Segundo apellido"
									name="surname2"
									value={form.fields.surname2.value}
									onChange={handleChange}
									onBlur={handleBlurField}
								/>
							</InputContainer>
						</Container>
					</FlexContainerSingle>
				</Container>

				<InputContainerDouble className="document-container">
					<Container
						data-testid="register-form__document-container"
						wrap={{ [Breakpoints.default]: 'always', [Breakpoints.b560]: 'never' }}
						gutter={{
							[Breakpoints.default]: ContainerGutter.dynamic0130,
							[Breakpoints.b560]: ContainerGutter.static400,
							[Breakpoints.b960]: ContainerGutter.static400,
						}}
						horizontalAlign={'space-between'}
						stretchContent
					>
						<Select
							defaultValue={typeDocumentOptions[0].value}
							appearance={hasFormFieldError(form.fields.typeDocument) ? ('error' as any) : undefined}
							label={'Tipo de documento*'}
							name="typeDocument"
							value={form.fields.typeDocument.value}
							onChange={handleChange}
							onFocus={handleBlurField}
						>
							{generateSelectOptions(typeDocumentOptions)}
						</Select>

						<TextInput
							appearance={hasFormFieldError(form.fields.document) ? 'error' : undefined}
							label="Número de documento*"
							name="document"
							value={form.fields.document.value}
							onChange={handleChange}
							onBlur={handleBlurField}
						/>
					</Container>
				</InputContainerDouble>
			</Container>
		</Layout>
	);
};
